import { formatNumber } from '@angular/common';
import { ProgressBarData } from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { InsightsSummaryDataKpi } from '../../coaching-insight/insight-widget/model/insights-summary-data-kpi.model';
import { KpiType } from '../../coaching-insight/insight-widget/model/kpi-type.model';
import { epicDependencies } from '../../store';
import { CardItem } from '../models/card-item.model';
import { CardRowData } from '../models/card-row-data.model';
import { Mi2DataResult } from '../models/mi2-data-result';
import { RowType } from '../models/row-type.enum';
import { TranslationKeyMap } from '../my-coaching-card.i18n';

import { calculateTrendValues } from './get-card-row-data.function';
import { getHeaderRowData } from './get-header-row-data.function';
import { getProgressDataMi2 } from './get-progress-data-mi2.function';
const formatValue = (value: number): string => {
    if (value < 10) {
        return formatNumber(value, 'en-CA', '1.2-2');
    } else if (value < 100) {
        return formatNumber(value, 'en-CA', '1.1-1');
    }
    return formatNumber(value, 'en-CA', '1.0-0');
};
export function getMi2Item(
    mi2DataResult: Mi2DataResult | null,
    kpiType: InsightsSummaryDataKpi,
    superGroupScore?: number,
): CardItem {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);

    const dates = mi2DataResult?.dates;
    const { startDate, endDate } = dates ?? {
        startDate: new Date(),
        endDate: new Date(),
    };

    const headerData = getHeaderRowData(startDate, endDate, kpiType.kpiTypeId);
    const isFlMi2 =
        kpiType.kpiTypeId === KpiType.FL_MANAGEMENT_IMPROVEMENT_INDEX;
    const rowType = isFlMi2 ? RowType.FLMI2 : RowType.MI2;
    const dataRowNameLabel = translateService.instant(
        isFlMi2
            ? TranslationKeyMap._11118_FRONT_LINE_MI2
            : TranslationKeyMap._798_MI2,
    );

    if (
        mi2DataResult == null ||
        Object.keys(mi2DataResult).length === 0 ||
        mi2DataResult.miScoreDataList.length === 0
    ) {
        const noDataMessage = translateService.instant(
            TranslationKeyMap._9866_NO_MI2_DATA_AVAILABLE,
        );
        const metricNameTooltip = `${String(
            translateService.instant(TranslationKeyMap._11177_LAUNCH),
        )} ${String(dataRowNameLabel)}`;
        return {
            headerData,
            cardRowsData: [
                {
                    metricPerformanceData: {
                        metricName: dataRowNameLabel,
                        metricNameTooltip,
                        trendValues: null,
                        progressBarData: null,
                        color: '#000000',
                        noActualDataMessage: null,
                        noTargetDataMessage: null,
                        noDataMessage,
                        isVisible: false,
                        isExpandable: false,
                    },
                },
            ],
            type: rowType,
        };
    }

    const { miScoreDataList } = mi2DataResult;
    const [miScoreData] = miScoreDataList;
    const {
        actual: currentActual,
        previous: previousActual,
        thresholdColor,
        target,
    } = miScoreData;

    const trendValues = calculateTrendValues(
        currentActual,
        previousActual,
        thresholdColor,
        translateService,
    );

    const thresholds = getProgressDataMi2(mi2DataResult);
    const actualValue = Number.isFinite(currentActual)
        ? Math.round(currentActual)
        : 0;
    const progressBarData: ProgressBarData = {
        thresholds,
        actualValue: {
            title: String(
                translateService.instant(TranslationKeyMap._725_ACTUAL),
            ),
            value: actualValue,
            tooltipText: `${String(
                translateService.instant(TranslationKeyMap._725_ACTUAL),
            )}: ${String(actualValue)}`,
        },
        targetValue: {
            title: String(
                translateService.instant(TranslationKeyMap._726_TARGET),
            ),
            value: target ?? 0,
            tooltipText: `${String(
                translateService.instant(TranslationKeyMap._726_TARGET),
            )}: ${String(formatValue(target ?? 0))}`,
        },

        minValue: 0,
        maxValue: 100,
    };
    const metricNameTooltip = `${String(
        translateService.instant(TranslationKeyMap._11177_LAUNCH),
    )} ${String(dataRowNameLabel)}`;
    const metricPerformanceData = {
        metricName: dataRowNameLabel,
        metricNameTooltip,
        trendValues,
        progressBarData,
        color: thresholdColor,
        noActualDataMessage:
            currentActual == null
                ? translateService.instant(
                      TranslationKeyMap._11172_NO_RESULTS_TO_DISPLAY,
                  )
                : null,
        noTargetDataMessage:
            target == null
                ? translateService.instant(
                      TranslationKeyMap._11173_NO_TARGET_THRESHOLDS_CONFIGURED,
                  )
                : null,
        noDataMessage: null,
        isVisible: true,
        isExpandable: false,
    };

    const cardRowsData: CardRowData[] = [
        {
            metricPerformanceData,
        },
    ];

    return {
        headerData,
        cardRowsData,
        type: rowType,
    };
}
